import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { HttpService } from '../http.service';
import { HttpErrorHandlerService } from '../http-error-handler.service';
import { environment } from '../../../environments/environment';
import { endpoints } from '../../../environments/endpoints';

@Injectable({
  providedIn: 'root'
})
export class CategoriesService {
  private apiUrl = `${environment.api.categories}`;

  constructor(
    private httpService: HttpService,
    private errorHandler: HttpErrorHandlerService
  ) { }

  /**
   * Lista las categorías disponibles con soporte para scroll infinito.
   * @param page Número de página actual (por defecto 1)
   * @param perPage Cantidad de elementos por página (por defecto 10)
   * @param sortField Campo por el que ordenar (por defecto 'name')
   * @param sortOrder Dirección de ordenamiento (1: ascendente, -1: descendente)
   * @returns Observable con las categorías y metadata para paginación
   */
  getCategories(page: number = 1, perPage: number = 10, sortField: string = 'order', sortOrder: number = 1): Observable<any> {
    // Default to requesting persistent order ascending
    const orderDir = sortOrder === 1 ? 'asc' : 'desc';
    return this.httpService.get(`${this.apiUrl}?page=${page}&per_page=${perPage}&sort=${sortField}&order=${orderDir}`).pipe(
      map((response: any) => {
        return {
          categories: response.data,
          pagination: {
            currentPage: response.pagination.current_page,
            lastPage: response.pagination.last_page,
            perPage: response.pagination.per_page,
            total: response.pagination.total,
            hasMorePages: response.pagination.has_more_pages
          },
        };
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene las subcategorías de una categoría específica.
   * @param categoryId ID de la categoría padre
   * @returns Observable con las subcategorías de la categoría especificada
   */
  getSubcategories(categoryId: number): Observable<any> {
    return this.httpService.get(`${this.apiUrl}/${categoryId}/subcategories`).pipe(
      map((response: any) => {
        return response.data || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene las subcategorías de una categoría específica con paginación.
   * @param categoryId ID de la categoría padre
   * @param page Número de página actual (por defecto 1)
   * @param perPage Cantidad de elementos por página (por defecto 10)
   * @param sortField Campo por el que ordenar (por defecto 'name')
   * @param sortOrder Dirección de ordenamiento (1: ascendente, -1: descendente)
   * @returns Observable con las subcategorías y metadata para paginación
   */
  getSubcategoriesByCategoryId(categoryId: number, page: number = 1, perPage: number = 10, sortField: string = 'name', sortOrder: number = 1): Observable<any> {
    const orderDir = sortOrder === 1 ? 'asc' : 'desc';
    return this.httpService.get(`${this.apiUrl}/${categoryId}/subcategories?page=${page}&per_page=${perPage}&sort=${sortField}&order=${orderDir}`).pipe(
      map((response: any) => {
        return {
          subcategories: response.data,
          pagination: {
            currentPage: response.pagination.current_page,
            lastPage: response.pagination.last_page,
            perPage: response.pagination.per_page,
            total: response.pagination.total,
            hasMorePages: response.pagination.has_more_pages
          },
        };
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Guarda la categoría seleccionada en el servicio
   * @param category Categoría seleccionada por el usuario
   */
  selectCategory(category: any): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/select`, { category }).pipe(
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene una lista de categorías activas optimizada para mostrar en componentes select
   * @returns Observable con un array de categorías activas en formato {id, name}
   */
  getActiveCategoriesForSelect(): Observable<any[]> {
    return this.httpService.get(`${this.apiUrl}/active`).pipe(
      map((response: any) => {
        const list = Array.isArray(response.data) ? response.data : [];
        // Defensive: sort by persistent order field before mapping
        list.sort((a: any, b: any) => ( (a.order || 0) - (b.order || 0) ));
        return list.map((category: any) => ({
          id: category.id,
          name: category.name
        })) || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene una lista de subcategorías activas de una categoría específica
   * optimizada para mostrar en componentes select
   * @param categoryId ID de la categoría padre
   * @returns Observable con un array de subcategorías activas en formato {id, name}
   */
  getActiveSubcategoriesForSelect(categoryId: number): Observable<any[]> {
    return this.httpService.get(`${this.apiUrl}/${categoryId}/subcategories/active`).pipe(
      map((response: any) => {
        return response.data.map((subcategory: any) => ({
          id: subcategory.id,
          name: subcategory.name,
          info_visibility: subcategory.info_visibility,
          mask_info: subcategory.mask_info
        })) || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene una lista de atributos activos filtrados por categoría y subcategoría
   * optimizada para mostrar en componentes select
   * @param categoryId ID de la categoría
   * @param subcategoryId ID de la subcategoría
   * @returns Observable con un array de atributos activos en formato {id, name, type}
   */
  getActiveAttributesForSelect(categoryId: number, subcategoryId: number): Observable<any[]> {
    return this.httpService.get(`${this.apiUrl}/${categoryId}/subcategories/${subcategoryId}/attributes/active`).pipe(
      map((response: any) => {
        return response.data.map((attribute: any) => ({
          id: attribute.id,
          name: attribute.name,
          type: attribute.type || 'text' // Asegurarse de obtener el tipo
        })) || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene los atributos activos asociados a la categoría (no subcategoría)
   * útil para recuperar atributos globales de la categoría y combinarlos con los de la subcategoría
   */
  getActiveAttributesForCategorySelect(categoryId: number): Observable<any[]> {
    return this.httpService.get(`${this.apiUrl}/${categoryId}/attributes/active`).pipe(
      map((response: any) => {
        const list = Array.isArray(response.data) ? response.data : [];
        return list.map((attribute: any) => ({
          id: attribute.id,
          name: attribute.name,
          type: attribute.type || 'text'
        })) || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }
}
